import { IconButton } from '@material-ui/core';
import {
  getAgentInLenderContextByAgentId,
  getAgentInSinglePaymentProviderContextByAgentId,
  getLendersByMerchantId,
} from '@nemuru-eng/js-api-client';
import { MouseEvent } from 'react';
import { useParams } from 'react-router-dom';

import { AgentInLenderContextTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInLenderContextTable/AgentInLenderContextTable';
import { AgentInSinglePaymentProviderContextTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInSinglePaymentProviderContextTable/AgentInSinglePaymentProviderContextTable';
import { CredentialsType } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/CredentialsType';
import { AddCredentialsDialogFactory } from '@/components/modules/AgentDetail/ProvidersDetails/shared/AddCredentialsDialogFactory';
import { LoadingValuesWarning } from '@/components/modules/Documentation/components/shared/LoadingValuesWarning';
import { Icon } from '@/components/shared/components/Icon';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useGetMerchantStructureById } from '@/hooks/requests/merchant/useGetMerchantStructureById';
import { useCache } from '@/hooks/requests/useCache';
import { useSetPageLoadingWhenRequesting } from '@/hooks/requests/useSetPageLoadingWhenRequesting';

export const REVALIDATE_AGENT_IN_LENDER_CONTEXT_LIST_ID = 'revalidate-agent-in-lender-context-list';

interface AgentInLenderContextModuleProps {
  selectedCredentialsType: CredentialsType;
  canEdit?: boolean;
}

export const Credentials = ({ canEdit = false, selectedCredentialsType }: AgentInLenderContextModuleProps) => {
  const { opened, toggleDialog, setOpened } = useDialog();
  const { agentId, merchantId, channelId } = useParams();
  if (!merchantId || !agentId || !channelId) return null;
  const { merchantStructure, loading: loadingStructure } = useGetMerchantStructureById(merchantId);
  const { data: lenders, loading: loadingLenders } = useCache(
    getLendersByMerchantId,
    { merchantId },
    { fallbackData: [] },
  );
  const {
    data: agentInLenderContext,
    loading: loadingCredentials,
    revalidate: revalidateAgentInLenderContext,
  } = useCache(getAgentInLenderContextByAgentId, {
    agentId,
  });

  const {
    data: agentInSinglePaymentProviderContext,
    loading: loadingSinglePaymentProviderCredentials,
    revalidate: revalidateAgentInSinglePaymentProviderContext,
  } = useCache(
    getAgentInSinglePaymentProviderContextByAgentId,
    {
      agentId,
    },
    { fallbackData: [] },
  );

  useSetPageLoadingWhenRequesting('MERCHANT_STRUCTURE', [loadingStructure]);
  useSetPageLoadingWhenRequesting('LENDERS', [loadingLenders]);

  const isCredentialsLoading =
    selectedCredentialsType === CredentialsType.AGENT_IN_LENDER_CONTEXT
      ? loadingCredentials
      : loadingSinglePaymentProviderCredentials;

  const specificActionsOptionsArray: ActionsOption[] | undefined = canEdit
    ? [
        {
          display: <Text tId={'ADD_CREDENTIALS'} />,
          onClick: () => setOpened(true),
          iconName: 'add',
        },
      ]
    : [];
  if (!lenders || !merchantStructure) return null;

  const revalidateCredentials = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (selectedCredentialsType === CredentialsType.AGENT_IN_LENDER_CONTEXT) {
      revalidateAgentInLenderContext();
      return;
    }
    revalidateAgentInSinglePaymentProviderContext();
  };

  return (
    <>
      <SubSectionWrapper
        title={`${selectedCredentialsType}`}
        specificActionsOptionsArray={specificActionsOptionsArray}
        rightComponent={
          <IconButton id={REVALIDATE_AGENT_IN_LENDER_CONTEXT_LIST_ID} type="button" onClick={revalidateCredentials}>
            <Icon size={20}>refresh</Icon>
          </IconButton>
        }
      >
        {isCredentialsLoading ? (
          <LoadingValuesWarning
            boxProps={{ height: '66px', padding: '26px', width: '100%', justifyContent: 'center !important' }}
          />
        ) : (
          <>
            {selectedCredentialsType === CredentialsType.AGENT_IN_LENDER_CONTEXT && (
              <AgentInLenderContextTable
                agentId={agentId}
                channelId={channelId}
                lenders={lenders}
                merchantStructure={merchantStructure}
                agentsInLenderContextCredentials={agentInLenderContext}
                credentialsType={CredentialsType.AGENT_IN_LENDER_CONTEXT}
              />
            )}
            {selectedCredentialsType === CredentialsType.AGENT_IN_SINGLE_PAYMENT_PROVIDER_CONTEXT && (
              <AgentInSinglePaymentProviderContextTable
                agentId={agentId}
                channelId={channelId}
                lenders={lenders}
                merchantStructure={merchantStructure}
                agentsInSinglePaymentProviderContext={agentInSinglePaymentProviderContext}
                credentialsType={CredentialsType.AGENT_IN_SINGLE_PAYMENT_PROVIDER_CONTEXT}
              />
            )}
          </>
        )}
      </SubSectionWrapper>
      {opened && (
        <AddCredentialsDialogFactory
          credentialsType={selectedCredentialsType}
          opened={opened}
          setOpened={setOpened}
          toggleDialog={toggleDialog}
          agentId={agentId}
          channelId={channelId}
          lenders={lenders}
          merchantStructure={merchantStructure}
        />
      )}
    </>
  );
};
